import { colors } from '@apollo/brand';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  customColors,
} from '@apollo/orbit';
import React, { useMemo } from 'react';

import { InsightsLineChartTooltip } from 'src/app/graph/insights/InsightsLineChartTooltip';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import {
  LineChart,
  LineChartStackConfig,
} from 'src/components/charts/LineChart';
import {
  Resolution,
  UI__ErroredTracePathsQuery_service_histograms_queryStats,
} from 'src/lib/graphqlTypes/types';

export function ErrorsChart({
  reports,
  resolution,
}: {
  reports:
    | UI__ErroredTracePathsQuery_service_histograms_queryStats[]
    | undefined
    | null;
  resolution: Resolution;
}) {
  const lineChartStacks = useMemo(
    (): LineChartStackConfig<UI__ErroredTracePathsQuery_service_histograms_queryStats>[] => [
      {
        colorGradientTop: colors.primitives.gray[200],
        colorGradientBottom: `${colors.primitives.gray[200]}00`,
        primaryColor: customColors.code.a,
        label: 'Requests',
        data: reports ?? [],
        getValue: ({
          timestamp,
          metrics: { cachedRequestsCount, uncachedRequestsCount },
        }: UI__ErroredTracePathsQuery_service_histograms_queryStats) => ({
          timestamp,
          value: cachedRequestsCount + uncachedRequestsCount,
        }),
      },
    ],
    [reports],
  );

  const minorLineChartStacks = useMemo(
    (): LineChartStackConfig<UI__ErroredTracePathsQuery_service_histograms_queryStats>[] => [
      {
        primaryColor: customColors.code.g,
        label: 'Errors',
        data: reports ?? [],
        getValue: ({
          timestamp,
          metrics: { requestsWithErrorsCount },
        }: UI__ErroredTracePathsQuery_service_histograms_queryStats) => ({
          timestamp,
          value: requestsWithErrorsCount,
        }),
      },
    ],
    [reports],
  );

  if (!reports) return <div>Loading...</div>;

  return (
    <Card variant="unstyled">
      <CardHeader>
        <CardTitle>Error count over time</CardTitle>
      </CardHeader>
      <CardBody>
        <div className="flex">
          <div className="flex-1">
            <div
              className="mr-2 inline-block size-3 rounded-full"
              style={{ background: customColors.code.g }}
            />
            <span>Errors</span>
          </div>
          <div className="flex-none">
            <span>Requests</span>
            <div
              className="ml-2 inline-block size-3 rounded-full"
              style={{ background: customColors.code.a }}
            />
          </div>
        </div>
        <CursorProvider>
          <LineChart
            stacks={lineChartStacks}
            minorStacks={minorLineChartStacks}
            stacked
            className="h-40"
            strokeWidth={1}
            resolution={resolution}
            tooltipLabelAs={<InsightsLineChartTooltip />}
            hideCursorArrow
          />
        </CursorProvider>
      </CardBody>
    </Card>
  );
}
